import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Activity,
  ShieldCheck,
  TrendingUp,
  Users,
  ClipboardCheck,
  LineChart,
} from "lucide-react";
import { Button } from "../components/ui/button";

const tierStrip = [
  { label: "Critical", range: "0–25", color: "tier-critical" },
  { label: "Exposed", range: "26–50", color: "tier-exposed" },
  { label: "Performing", range: "51–75", color: "tier-performing" },
  { label: "Elite", range: "76–100", color: "tier-elite" },
];

const features = [
  {
    icon: <ClipboardCheck className="w-5 h-5" />,
    title: "20-Question Wellness Audit",
    body: "A premium swipe-based experience covering Sleep, Nutrition, Stress, Movement, Recovery and more.",
  },
  {
    icon: <LineChart className="w-5 h-5" />,
    title: "Tiered Scoring & Insights",
    body: "Each respondent is scored 0–100 and tiered: Critical, Exposed, Performing, or Elite.",
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: "License Holder Dashboard",
    body: "Capture leads, reveal opted-in emails, and download 7/14/30-day reports as PDF or CSV.",
  },
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: "GDPR-Ready & Medical Gated",
    body: "Mandatory disclaimer acceptance, opt-in capture, and UK-compliant data handling.",
  },
  {
    icon: <Activity className="w-5 h-5" />,
    title: "Branded 3-Page PDF Report",
    body: "Each respondent receives a beautiful, on-brand result PDF — generated instantly.",
  },
  {
    icon: <TrendingUp className="w-5 h-5" />,
    title: "Workforce / Member Retention",
    body: "Dial in retention, identify at-risk members, and prove ROI to stakeholders.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div
          className="absolute -top-40 -right-32 w-[640px] h-[640px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #00BFFF 0%, transparent 60%)" }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[640px] h-[640px] rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="eyebrow" data-testid="hero-eyebrow">Enterprise Wellness Intelligence</span>
            <h1 className="mt-5 font-display font-medium tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              Score, segment, and <span className="shine-text">retain</span> the people who power your business.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Fitest is the wellness audit platform built for high-performance gyms and UK enterprises.
              Capture leads, surface at-risk members, and prove ROI in days, not quarters.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/request-demo">
                <Button className="btn-glow rounded-full h-12 px-7" data-testid="hero-cta-demo">
                  Request a Demo <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button className="btn-ghost-bordered rounded-full h-12 px-7" data-testid="hero-cta-pricing">
                  See Pricing
                </Button>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-3" data-testid="tier-strip">
              {tierStrip.map((t) => (
                <div
                  key={t.label}
                  className={`px-3 py-1.5 rounded-full text-xs border ${t.color} flex items-center gap-2`}
                >
                  <span className="font-mono">{t.range}</span>
                  <span className="opacity-80">{t.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="features-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="eyebrow">The platform</span>
            <h2 className="font-display tracking-tight text-3xl sm:text-4xl mt-3 max-w-xl">
              A swipe audit, a scoring engine, and a dashboard your retention team will love.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, idx) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.45, delay: idx * 0.05 }}
              className="glass rounded-2xl p-6 h-full hover:border-[var(--border-active)] transition-colors"
              data-testid={`feature-${idx}`}
            >
              <div className="w-10 h-10 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-brand mb-5">
                {f.icon}
              </div>
              <h3 className="font-display text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="how-it-works">
        <span className="eyebrow">How it works</span>
        <h2 className="font-display tracking-tight text-3xl sm:text-4xl mt-3 max-w-2xl">
          Live in 1–2 business days. Insights you can act on by the end of the week.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {[
            { n: "01", t: "Generate your audit link", b: "Drop your unique fitest.co.uk/audit/[slug] into your member or staff comms." },
            { n: "02", t: "Members swipe through 20 questions", b: "A premium mobile-first audit covering all dimensions of wellness." },
            { n: "03", t: "You get scored, tiered leads", b: "Reveal opted-in emails, download reports, and re-engage at the right moment." },
          ].map((s) => (
            <div key={s.n} className="glass rounded-2xl p-6" data-testid={`step-${s.n}`}>
              <div className="font-mono text-xs text-brand mb-3">{s.n}</div>
              <h3 className="font-display text-xl mb-2">{s.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div
          className="rounded-3xl p-10 md:p-16 relative overflow-hidden border border-white/10"
          style={{
            background:
              "radial-gradient(900px 400px at 80% 100%, rgba(0, 191, 255, 0.18), transparent 50%), radial-gradient(700px 300px at 0% 0%, rgba(37, 99, 235, 0.18), transparent 50%), #0D1B2A",
          }}
          data-testid="cta-strip"
        >
          <h2 className="font-display tracking-tight text-3xl sm:text-4xl max-w-2xl">
            Ready to turn wellness into a measurable lever?
          </h2>
          <p className="text-slate-300 mt-3 max-w-xl">
            Book a 20-minute demo and we'll show you a live dashboard with your branding.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-demo"><Button className="btn-glow rounded-full h-12 px-7" data-testid="cta-demo">Request Demo</Button></Link>
            <Link to="/pricing"><Button className="btn-ghost-bordered rounded-full h-12 px-7" data-testid="cta-pricing">View Licenses</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
